import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AIModel } from '../types';
import { setUserModel, fetchUser } from '../api';
import styles from './ChatApp.module.css';
import { AxiosError } from 'axios';

interface ModelSelectorProps {
  models: AIModel[];
  currentModel: string;
  onModelChange: (modelSystemName: string) => void;
}

const ModelSelector: React.FC<ModelSelectorProps> = ({ models, currentModel, onModelChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(currentModel);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);
  const navigate = useNavigate();

  const toggleModels = () => setIsOpen((prev) => !prev);

  const selectModel = async (modelSystemName: string) => {
    try {
      const userData = await fetchUser();
      if (!userData.is_authenticated) {
        navigate('/login');
        setIsOpen(false);
        return;
      }
      const data = await setUserModel(modelSystemName);
      setSelectedModel(data.current_model);
      onModelChange(data.current_model);
      setHoveredModel(null);
      setIsOpen(false);
    } catch (error) {
      console.error('Error in selectModel:', error);
      if ((error as AxiosError).response?.status === 403) {
        navigate('/login');
        setIsOpen(false);
      }
    }
  };

  const filteredModels = models.filter((model) =>
    model.display_name?.toLowerCase().includes(searchQuery.toLowerCase() || '')
  );

  useEffect(() => {
    setSelectedModel(currentModel);
  }, [currentModel]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (target && !target.closest(`.${styles.modelSelector}`)) {
        setIsOpen(false);
        setHoveredModel(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className={styles.modelSelector}>
      <button
        className={`${styles.modelSelectorBtn} ${isOpen ? styles.open : ''}`}
        onClick={toggleModels}
      >
        <span id="selected-model">
          {models.find((m) => m.system_name === selectedModel)?.display_name || selectedModel || 'Выберите модель'}
        </span>
        <i className="fas fa-chevron-down"></i>
      </button>
      {isOpen && (
        <div id="modelsDropdown" className={styles.modelsDropdown}>
          <div className={styles.dropdownSearch}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Поиск моделей..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className={styles.modelsList}>
            {filteredModels.map((model) => (
              <div
                className={`${styles.modelItem} ${model.system_name === selectedModel ? styles.activeModel : ''}`}
                key={model.system_name}
                data-model-name={model.system_name}
                onClick={() => selectModel(model.system_name)}
                onMouseEnter={() => setHoveredModel(model.system_name)}
                onMouseLeave={() => setHoveredModel(null)}
              >
                <span>{model.display_name}</span>
                <span className={styles.modelPrice}>
                  {model.price_per_million_tokens == 0
                    ? 'Бесплатно'
                    : model.price_per_million_tokens !== undefined
                      ? `${Math.floor(model.price_per_million_tokens)} ₽/млн токенов`
                      : 'Цена неизвестна'}
                </span>
                {model.supports_images && (
                  <span className={styles.modelSupportsImages}>
                    <i className="fas fa-image"></i> Поддержка изображений
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {hoveredModel && isOpen && (
        <div
          id="modelDescriptionPopup"
          className={styles.modelDescriptionPopup}
          style={{
            top: `${document
                .querySelector(`[data-model-name="${hoveredModel}"]`)
                ?.getBoundingClientRect().top || 0
              }px`,
            left: `${(document
                .querySelector(`[data-model-name="${hoveredModel}"]`)
                ?.getBoundingClientRect().right || 0) + 10
              }px`,
          }}
        >
          {(() => {
            const hoveredModelData = models.find((m) => m.system_name === hoveredModel);
            return (
              <>
                <h4 className={styles.modelPopupName}>
                  {hoveredModelData?.display_name || 'Неизвестная модель'}
                </h4>
                <p className={styles.modelPopupPrice}>
                  Цена: {hoveredModelData?.price_per_million_tokens == 0
                    ? 'Бесплатно'
                    : hoveredModelData?.price_per_million_tokens !== undefined
                      ? `${Math.floor(hoveredModelData.price_per_million_tokens)} ₽/млн токенов`
                      : 'Цена неизвестна'}
                </p>
                {hoveredModelData?.supports_images && (
                  <p className={styles.modelPopupSupportsImages}>
                    <i className="fas fa-image"></i> Поддерживает изображения
                  </p>
                )}
                <p className={styles.modelPopupDescription}>
                  {hoveredModelData?.description || 'Нет описания'}
                </p>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default ModelSelector;